import { deviceLocal } from '@/privacy/deviceStorage';
import { reaction } from 'mobx';
import { subscribeToMyAttention, wakeMyAttention } from '@/local-sync/myAttention';
import { subscribeToMyWorkAreas, wakeMyWorkAreas } from '@/local-sync/myWorkAreas';
import { AuthStore } from './AuthStore';
import { ProjectStore } from './ProjectStore';
import { ViewStore } from './ViewStore';
import { NotificationStore } from './NotificationStore';
import { loadWorkItemStore, workItemStoreOrThrow } from './workItemStoreSlot';
import {
  aiSuggestionStoreSlot,
  commentStoreSlot,
  feedbackStoreSlot,
  labelStoreSlot,
  openBranchesStoreSlot,
} from './lazyStores';
import { workItemViewStoreSlot } from './workItemViewStoreSlot';
import { DashboardPageStore } from './DashboardPageStore';
import { ViewHierarchyStore } from './ViewHierarchyStore';
import { DecisionsStore } from './DecisionsStore';
import { DecisionAnswerToastStore } from './DecisionAnswerToastStore';
import { ItemArchiveToastStore } from './ItemArchiveToastStore';
import { ItemCreatedToastStore } from './ItemCreatedToastStore';
import { TasksStore } from './TasksStore';
import { InvitationsWaitingStore } from './InvitationsWaitingStore';
import { NamespaceStore } from './NamespaceStore';
import { ViewChoicesStore } from './ViewChoicesStore';
import { viewChoicesApi } from '@/services/ViewChoicesApi';
import { AppearanceStore } from './AppearanceStore';
import { WaitingAnimationStore } from './WaitingAnimationStore';
import { appearanceApi, waitingAnimationApi } from '@/services/AppearanceApi';
import { afterBootRequests } from '@/services/bootRequests';
// T-0472: the language setting's server side (`i18n/languageSyncInstance.ts`) is a chunk of its own, asked for
// after the session check and the launch's own reads (`afterBootRequests`): a chunk fetched before them waits
// ahead of them on a busy connection (I-1380, `launch-tab-boot.spec.ts`).
// One chain, so a person's `load` is never run before the earlier `forget` of nobody: two chains resolve in
// no promised order, and a `forget` that came second dropped the `load` in flight.
let languageSyncQueue: Promise<unknown> = Promise.resolve();
const languageSync = () =>
  afterBootRequests()
    .then(() => import('@/i18n/languageSyncInstance'))
    .then((module) => module.languageSync);
import { WorkAreaScope } from './workAreaScope';
import {
  SESSION_END_CAUSES,
  TOKEN_KEY,
  endSession,
  hasStoredAuthSession,
  onSessionEnd,
} from '@/services/ApiService';
import { registerShellPushAtLaunch } from '@/shell/pushRegistration';
import { onShellEvent } from '@/shell/shellBridge';
import { removeDataWhereRequired } from '@/privacy/signOutChoice';
import { subscribeToWorkAreaRealtime } from '@/local-sync/workItemRealtime';
import { onWorkAreaDeletedElsewhere } from './workAreaDeletedChannel';

export const authStore = new AuthStore();
/**
 * I-1525 (AC-19.19): the work-area menu follows the person's own work areas — a ring of
 * `myWorkAreasChanges`, a resumed stream, the window coming back into focus — without a reload.
 */
export const projectStore = new ProjectStore(undefined, {
  subscribe: subscribeToMyWorkAreas,
  wake: wakeMyWorkAreas,
  onForeground: (listener) =>
    onShellEvent((event) => {
      if (event.type === 'foreground') listener();
    }),
});
/** F-0363 slice 2: the work area's custom views and folders, as a tree (D-0241: a). */
export const viewHierarchyStore = new ViewHierarchyStore();
/**
 * AC-19: `viewStore.savedViews`/`loading` read `viewHierarchyStore` directly (the one holder),
 * passed here rather than imported inside `ViewStore.ts` to avoid a cycle with `stores/index.ts`
 * owning both singletons. AC-16's own nudge stays for this store's write path (`saveCurrentView`/
 * `deleteView`, `SavedViewSelector.tsx`'s own): a save or delete made through it still has to tell
 * `viewHierarchyStore` its copy changed. `CustomViewsPage.tsx`'s own write path calls the same
 * store directly, since it already holds every other store through `useStores()`.
 */
export const viewStore = new ViewStore(
  (projectId) => void viewHierarchyStore.refresh(projectId),
  viewHierarchyStore,
);
/** F-0236: the bell follows the person's own attention stream, and polls as a backstop. */
export const notificationStore = new NotificationStore({
  subscribeToAttention: subscribeToMyAttention,
  wakeAttention: wakeMyAttention,
});
export const dashboardPageStore = new DashboardPageStore();
/**
 * Handed the live channel: a decision filed for or answered by the reader rings the work area's
 * shared stream, and the badge and tab title follow within a second (I-0635, AC-2).
 */
export const decisionsStore = new DecisionsStore({
  subscribeToWorkArea: subscribeToWorkAreaRealtime,
  subscribeToAttention: subscribeToMyAttention,
});
/** IU-8: the answer toast, outside `DecisionsStore` so navigating away does not unmount it. */
export const decisionAnswerToastStore = new DecisionAnswerToastStore();
/** IU-9 (F-0510): Archive's own Undo toast, outside `WorkItemStore` for the same reason. */
export const itemArchiveToastStore = new ItemArchiveToastStore({
  // T-0770 (LC-6c): an Undo loads the store if nothing has yet, never reads it before.
  unarchive: (itemId) => loadWorkItemStore().then((store) => store.unarchive(itemId)),
});
/** IU-10 (F-0510): Add's own toast, outside `WorkItemCreatePage` for the same reason. */
export const itemCreatedToastStore = new ItemCreatedToastStore();
export const namespaceStore = new NamespaceStore();
/** F-0579 ACC-1: Accents, Quiet or Colour — the person's, cached on the device for the first frame. */
export const appearanceStore = new AppearanceStore({
  userId: () => authStore.user?.id ?? null,
  api: appearanceApi,
  storage: () => {
    try {
      return typeof window === 'undefined' ? null : deviceLocal;
    } catch {
      return null;
    }
  },
  root: () => (typeof document === 'undefined' ? null : document.documentElement),
});
/** F-0578 WAIT-5: the Waiting animation, Random or one study — the person's, cached on the device. */
export const waitingAnimationStore = new WaitingAnimationStore({
  userId: () => authStore.user?.id ?? null,
  api: waitingAnimationApi,
  storage: () => {
    try {
      return typeof window === 'undefined' ? null : deviceLocal;
    } catch {
      return null;
    }
  },
});
/** F-0527 (ADR-165): a person's view choices, kept on the server unless they turned that off. */
export const viewChoicesStore = new ViewChoicesStore({
  userId: () => authStore.user?.id ?? null,
  api: viewChoicesApi,
  storage: () => {
    try {
      return typeof window === 'undefined' ? null : deviceLocal;
    } catch {
      return null;
    }
  },
  hasSession: () => {
    try {
      return hasStoredAuthSession();
    } catch {
      return false;
    }
  },
});
/**
 * "Tasks for me" (I-0900), read by the sidebar nav badge and the pages beneath it. Rides the same
 * per-person attention stream Decisions and the bell use, extended server-side with a `task` reason.
 */
export const tasksStore = new TasksStore({
  subscribeToAttention: subscribeToMyAttention,
});

/**
 * I-2233: the person's own pending invitations, read by the profile line's badge and the notice that
 * one is waiting. Its reads come after the launch's own (`afterBootRequests`), and the two APIs load
 * with the first read, not in the launch entry.
 */
export const invitationsWaitingStore = new InvitationsWaitingStore({
  userId: () => authStore.user?.id ?? null,
  api: {
    mine: () =>
      afterBootRequests()
        .then(() => import('@/services/AccessControlApi'))
        .then((module) => module.accessControlApi.myInvitations()),
    seen: () =>
      afterBootRequests()
        .then(() => import('@/services/UserPreferencesApi'))
        .then((module) => module.userPreferencesApi.invitationsSeen()),
    markSeen: (id) =>
      import('@/services/UserPreferencesApi').then((module) =>
        module.userPreferencesApi.markInvitationSeen(id),
      ),
  },
  subscribeToAttention: subscribeToMyAttention,
});

/**
 * I-0958 (`docs/concept-work-area-scope.md`): the stores that hold one work area's rows at a time.
 * `WorkAreaScopeGate` enters a new work area here before the page renders, so none of them draws the
 * previous work area's rows. `WORK_AREA_STORE_KINDS` says why every other store is not here.
 */
// T-0770 (LC-6c): `workItemStore` joins when it loads (`workItemStoreInstance.ts`, `join`).
export const workAreaScope = new WorkAreaScope([decisionsStore, tasksStore, viewStore]);

// F-0534 PPD-3: a session that ended in another tab or window of this origin (they share the tokens)
// ends in this one too.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== TOKEN_KEY) return;
    if (hasStoredAuthSession()) return;
    authStore.sessionEndedElsewhere();
  });
}

// F-0783 (§4d WAD-9): a work area deleted in another tab is re-read here at once; one this tab was
// in is then said to be gone (I-1876), and the scope falls back to All work areas.
if (typeof window !== 'undefined') {
  onWorkAreaDeletedElsewhere(() => projectStore.refreshWorkAreas());
}

// I-2102: every sign-out, from whichever screen, removes the person's data when their organisation
// requires it (the Sign out dialog asks first; a revoked device, the Terms screen, mobile pairing and a
// lost credential ask nobody).
authStore.onBeforeSignOut(() => removeDataWhereRequired(namespaceStore));

// I-1287: a view choice still waiting out its debounce is sent before the session is cleared.
authStore.onBeforeSignOut(() => viewChoicesStore.sendBeforeSignOut());

// F-0534 PPD-3 (I-1311): every session end, with its cause, from the one function that ends one.
onSessionEnd((cause) => viewChoicesStore.sessionEnded(cause));

// Who is signed in. A second person on a page that still held the first is a session end for the
// first, told like every other. I-1213 (AC-3): a person signed in opens on their own places, never
// the last person's; the owner rule drops the last person's stores there (F-0534 PPD-2).
reaction(
  () => authStore.user?.id ?? null,
  (id, previous) => {
    if (id && previous && id !== previous) endSession(SESSION_END_CAUSES.ANOTHER_PERSON);
    // The setting and the sync are one person's: unknown again for whoever is here now (F-0527).
    viewChoicesStore.forget();
    appearanceStore.forget();
    invitationsWaitingStore.forget();
    waitingAnimationStore.forget();
    // T-0472: the language setting is one person's too. One call for both steps: two separate imports
    // resolve in no promised order, and a `load` that ran before the `forget` was dropped by it.
    languageSyncQueue = languageSyncQueue
      .then(() => languageSync())
      .then((sync) => {
        sync.forget();
        if (id) void sync.load(id);
      })
      // A chunk that does not arrive (the page reloaded or left while it was fetched, or the network
      // dropped) leaves the language as it is, and is no unhandled rejection: WebKit reported it as a
      // page error on every reload during the launch (gate 747). The queue keeps running, so the next
      // change of person asks for the chunk again.
      .catch(() => undefined);
    if (id) {
      projectStore.adoptPerson(id);
      // F-0579 ACC-1: after the owner rule, so this person's own Accents are what the device caches.
      void appearanceStore.load();
      void waitingAnimationStore.load();
      // I-2233: whether invitations wait for this person, for the profile line's badge.
      void invitationsWaitingStore.refresh();
      // The notices the lists, their badges and Agents draw (F-0744, I-2382), read now rather than at
      // the attention loop's first tick 30 s on: the bell that read them on mount is gone. The read
      // also wakes the person's attention stream, which waits for proof of a session.
      void notificationStore.fetchUnreadCount();
      // F-0534 (I-1238): the phone registers again at every launch, quietly, once push is allowed.
      registerShellPushAtLaunch(id);
    }
  },
  { fireImmediately: true },
);

export const stores = {
  authStore,
  /** T-0892: built by the first screen that uses it (`feedbackStoreInstance.ts`); a file in the entry never reads it. */
  get feedbackStore() {
    return feedbackStoreSlot.orThrow();
  },
  projectStore,
  /** T-0892: built by the first screen that uses it (`aiSuggestionStoreInstance.ts`); a file in the entry never reads it. */
  get aiSuggestionStore() {
    return aiSuggestionStoreSlot.orThrow();
  },
  /** T-0892: built by the first screen that uses it (`labelStoreInstance.ts`); a file in the entry never reads it. */
  get labelStore() {
    return labelStoreSlot.orThrow();
  },
  viewStore,
  /** T-0892: built by the first screen that uses it (`commentStoreInstance.ts`); a file in the entry never reads it. */
  get commentStore() {
    return commentStoreSlot.orThrow();
  },
  notificationStore,
  /**
   * T-0770 (LC-6c): built by the first screen that uses it, which imports
   * `workItemStoreInstance.ts`. Read before then, it throws `WorkItemStoreNotLoadedError`; a file in
   * the entry uses `loadWorkItemStore()` or `peekWorkItemStore()` instead.
   */
  get workItemStore() {
    return workItemStoreOrThrow();
  },
  /**
   * T-0892 (a): built by the first screen that uses it (`workItemViewStoreInstance.ts`); a file in the
   * entry reads the search query from `workItemSearchQuery.ts`, or the slot's observable `peek()`.
   */
  get workItemViewStore() {
    return workItemViewStoreSlot.orThrow();
  },
  dashboardPageStore,
  viewHierarchyStore,
  decisionsStore,
  decisionAnswerToastStore,
  itemArchiveToastStore,
  itemCreatedToastStore,
  namespaceStore,
  viewChoicesStore,
  appearanceStore,
  waitingAnimationStore,
  /** T-0892: built by the first screen that uses it (`openBranchesStoreInstance.ts`); a file in the entry never reads it. */
  get openBranchesStore() {
    return openBranchesStoreSlot.orThrow();
  },
  tasksStore,
  invitationsWaitingStore,
};

export type RootStore = typeof stores;
